import type { IContent, IFeatureFamily, ISection, IView } from '../../../types/ITypes';
import { ECategoryType } from '../../data/Constants';
import { checkIsMultiNumericCategory } from './CategoryTreeHelper';

/**
 * Checks if a section is a SubmodelView type
 * @param {ISection} section - The section to check
 * @returns {boolean} True if the section is a SubmodelView, false otherwise
 */
export const checkIfSectionIsSubmodel = (section: ISection): boolean => {
  return section.type === ECategoryType.SubmodelView;
};

/**
 * Updates the master view with flags for expanding and collapsing sections having subsections
 * @param {IView} view - The master view containing all sections
 * @param {ISection} selectedSection - The section that was selected/clicked
 * @returns {IView | null} Updated master view with section flags or null if selectedSection id is undefined
 */
export const getViewWithFlag = (view: IView, selectedSection: ISection): IView | null => {
  if (selectedSection?.code === undefined) {
    return null;
  }

  const updateSections = (sections: ISection[], level: number = 0): ISection[] => {
    //looping the sections and update its properties
    //(ex: hasIcon- to show + icon , open: to collapse or expand the section )
    return sections.map((section) => {
      if (checkIfSectionIsSubmodel(section)) {
        return section;
      }
      // Increment level only for child sections
      const nextLevel = level + 1;

      // Update properties for parent sections
      const isMultiNumericCategory = checkIsMultiNumericCategory(section.code);
      const type = isMultiNumericCategory ? ECategoryType.MultiNumeric.toString() : section.type;
      let updatedSection = {
        ...section,
        type: type,
        hideAddIcon: isMultiNumericCategory, // hide add icon for multi numeric categories
        hideGeneral: checkIfSectionIsSubmodel(section) ? true : level >= 1, // show general category for only until first level
        open: section.code === selectedSection.code ? !section.open : section.open,
      };

      // Update properties for child sections if any
      if (section.sections && section.sections.length > 0) {
        updatedSection = {
          ...updatedSection,
          sections: updateSections(section.sections, nextLevel),
        };
      }

      return updatedSection;
    });
  };

  //call the updateSections method to update its properties
  const updatedSections = updateSections(view.sections);
  return { ...view, sections: updatedSections };
};

/**
 * Gets the description for a feature or feature family
 * @param {IContent | IFeatureFamily} item - The content item or feature family
 * @param {IFeatureFamily[]} featureFamilyList - List of all feature families
 * @returns {string} The type of the feature family or an empty string if not found
 */
export const getFeatureType = (
  item: IContent | IFeatureFamily,
  featureFamilyList: IFeatureFamily[]
): string => {
  // If it's already a FeatureFamily type, return its type
  if ('type' in item) {
    return item.type;
  }
  // If it's a Content type, find the matching feature family
  const featureFamily = featureFamilyList.find((ff) => ff.code === item.code);
  return featureFamily?.familyType ?? '';
};

/**
 * Determines the icon type from content icon object
 * @param {{ familyType: string; type: string }} contentIcon - The content icon object containing familyType and/or type
 * @returns {string | undefined} The familyType if available, otherwise the type, or undefined if neither exists
 */
export const getIconType = (contentIcon: { familyType: string; type: string }): string => {
  switch (true) {
    case contentIcon?.familyType !== undefined:
      return contentIcon.familyType;
    case contentIcon?.type !== undefined:
      return contentIcon.type;
    default:
      return contentIcon.type;
  }
};

/**
 * Creates updated category data with product code and normalized sections
 * @param {string} productId - The product code
 * @param {ISection[]} sections - Array of sections to normalize
 * @returns {{ productCode: string; sections: ISection[] }} Formatted category data object
 */
export const getUpdatedCategoryData = (
  productId: string,
  sections: ISection[]
): { productCode: string; sections: ISection[] } => {
  return {
    productCode: productId,
    sections: sections.map((section) => ({
      ...section,
      contents: section.contents ?? [],
      sections: section.sections ?? [],
    })),
  };
};

/**
 * Checks if a feature is inactive based on its code
 * @param {string} featureCode - The code of the feature to check
 * @param {IFeatureFamily[]} featureFamilyList - List of all feature families
 * @returns {boolean} True if the feature is explicitly inactive, false if active or not found
 */
export const checkIsFeatureActive = (
  featureCode: string,
  featureFamilyList: IFeatureFamily[]
): boolean => {
  return featureFamilyList.some((f) => f.code === featureCode && f.isActive);
};

/**
 * Creates updated category data with product code and normalized sections
 * @param {string} productId - The product code
 * @param {ISection[]} sections - Array of sections to normalize
 * @returns {{ productCode: string; sections: ISection[] }} Formatted category data object
 */
export const getViewSaveRequest = (
  productId: string,
  sections: ISection[],
  view: { code: string; description: string }
): { productCode: string; code: string; description: string; sections: any[] } => {
  const transformSection = (section: ISection): any => {
    if (checkIfSectionIsSubmodel(section)) {
      return section;
    }
    return {
      type: section.type === ECategoryType.MultiNumeric ? ECategoryType.Standard : section.type,
      code: section.code,
      description: section.description,
      sections: section.sections ? section.sections.map(transformSection) : [],
      contentCodes: section.contents ? section.contents.map((c) => c.code) : [],
    };
  };

  return {
    productCode: productId,
    code: view.code,
    description: view.description,
    sections: sections.map(transformSection),
  };
};
